import React, { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, AlertTriangle, ShieldAlert, ExternalLink } from "lucide-react";
import { sharedApi } from "@/lib/api";

const SESSION_KEY = "allsale.plateLookup.lastPlate";

/**
 * PLATE-LOOKUP-234: On the Car post-ad form, sellers can enter their NZ
 * plate and have make/model/year + attributes filled from CarJam in one
 * click (kit spec: CLASSIFIEDS_CAR_PLATE_LOOKUP_KIT_ITER234).
 *
 * Pure UI wiring: pre-fill is fully editable, stolen banner is a warning
 * (does NOT block posting — backend policy handles moderation), and the
 * response's `carjam_report_url` is rendered with `rel="noopener"` (never
 * `noreferrer`) so CarJam attributes the referrer for our 20% share.
 *
 * Iter-253: remember the last successfully-looked-up plate for this
 * browser session so sellers who navigate away and back don't have to
 * retype. Same-plate re-lookups don't burn a daily-cap credit (30-day
 * server cache), so this is convenience, not cost saving.
 */
export default function PlateLookupCard({ onPrefill, plateLookupConfig }) {
  const [plate, setPlate] = useState(() => {
    if (typeof window === "undefined") return "";
    try {
      return sessionStorage.getItem(SESSION_KEY) || "";
    } catch {
      return "";
    }
  });
  const [result, setResult] = useState(null);

  const mut = useMutation({
    mutationFn: async () => {
      const clean = plate
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, "")
        .slice(0, 8);
      if (!clean) throw new Error("Enter a plate.");
      const { data } = await sharedApi.get(`/classifieds/car-lookup/${clean}`);
      return data;
    },
    onSuccess: (data) => {
      if (data?.pending) {
        toast.info("CarJam is preparing the report — try again in about 30 seconds.");
        return;
      }
      setResult(data);
      if (data?.prefill) {
        onPrefill(data.prefill);
        toast.success("Fetched from CarJam — check & edit as needed.");
      }
      // Iter-253: persist the plate for the session so users returning
      // to the form (e.g. after uploading photos in a new tab) don't
      // have to retype it.
      try {
        const clean = plate.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 8);
        if (clean) sessionStorage.setItem(SESSION_KEY, clean);
      } catch {
        /* sessionStorage unavailable — silent no-op */
      }
    },
    onError: (err) => {
      const status = err?.response?.status;
      const rawDetail = err?.response?.data?.detail;
      const detailStr =
        typeof rawDetail === "string"
          ? rawDetail
          : rawDetail?.message || rawDetail?.msg || null;
      if (status === 401) {
        toast.error("Please sign in to look up a plate.");
      } else if (status === 429) {
        toast.error(
          detailStr ||
            "Daily lookup limit reached — fill the details manually or try tomorrow."
        );
      } else if (status === 404 || status === 422) {
        toast.error(
          detailStr || "Plate not found — fill the details manually."
        );
      } else if (status === 400) {
        toast.error(detailStr || "That plate looks invalid.");
      } else {
        toast.error(
          detailStr || err?.message || "Couldn't reach CarJam right now."
        );
      }
    },
  });

  return (
    <div
      className="rounded-2xl bg-white border border-[color:var(--brand-blue-wash)] p-5"
      data-testid="plate-lookup-card"
    >
      <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.14em] text-[color:var(--brand-blue)]">
        Have the plate?
      </div>
      <div className="text-slate-800 font-display text-lg font-semibold mt-0.5">
        Find Your Vehicle Details
      </div>
      <p className="text-xs text-slate-500 mt-1">
        {plateLookupConfig?.ui ||
          "Enter your NZ plate — we'll pre-fill make, model, year and specs. All fields stay editable."}
      </p>

      <div
        className="mt-3 flex flex-col sm:flex-row gap-2"
        data-testid="plate-lookup-input-row"
      >
        <input
          data-testid="plate-lookup-input"
          value={plate}
          onChange={(e) => setPlate(e.target.value.toUpperCase().slice(0, 10))}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              // Prevent the outer post-ad form from submitting on Enter and
              // trigger the lookup instead.
              e.preventDefault();
              if (!mut.isPending && plate.trim()) mut.mutate();
            }
          }}
          placeholder="e.g. KRB300"
          className="input flex-1 uppercase tracking-widest font-mono font-bold"
          maxLength={10}
          autoCapitalize="characters"
          autoComplete="off"
        />
        <button
          type="button"
          onClick={() => {
            if (mut.isPending) return;
            mut.mutate();
          }}
          disabled={mut.isPending || !plate.trim()}
          data-testid="plate-lookup-fetch-btn"
          className="rounded-full bg-[color:var(--brand-blue)] hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold px-6 py-2.5 text-sm inline-flex items-center justify-center gap-2 transition-colors"
        >
          {mut.isPending ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" /> Fetching…
            </>
          ) : (
            "Get started"
          )}
        </button>
      </div>

      {result?.reported_stolen && (
        <div
          data-testid="plate-lookup-stolen-banner"
          role="alert"
          className="mt-3 rounded-xl border border-red-300 bg-red-50 px-3 py-2 flex items-start gap-2"
        >
          <ShieldAlert className="w-4 h-4 text-red-600 mt-0.5 shrink-0" />
          <div className="text-xs text-red-800">
            <span className="font-bold">This vehicle is reported stolen.</span>{" "}
            You can still post, but our safety team will review before publishing.
          </div>
        </div>
      )}

      {result?.prefill && !result?.reported_stolen && (
        <div className="mt-3 flex items-start gap-2 rounded-xl bg-[color:var(--brand-blue-wash)]/40 px-3 py-2">
          <AlertTriangle className="w-4 h-4 text-[color:var(--brand-blue)] mt-0.5 shrink-0" />
          <div className="text-xs text-slate-600">
            Details fetched from CarJam
            {result.cached ? " (cache)" : ""} — check &amp; edit before posting.
          </div>
        </div>
      )}

      {result?.carjam_report_url && (
        // eslint-disable-next-line react/jsx-no-target-blank -- CarJam affiliate REQUIRES referrer for attribution (20% share). Never noreferrer.
        <a
          href={result.carjam_report_url}
          target="_blank"
          rel="noopener"
          data-testid="plate-lookup-report-cta"
          className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-[color:var(--brand-blue)] hover:underline"
        >
          <ExternalLink className="w-3.5 h-3.5" /> View the full CarJam history report
        </a>
      )}
    </div>
  );
}
